import { useMemo, useState, useEffect } from "react";
import { NumericFormat } from "react-number-format";
import { useIntl } from "react-intl";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

/**
 * Props for the StandaloneFormattedInput component
 */
interface StandaloneFormattedInputProps {
  /** The current value of the input */
  value: string | number;
  /** Callback when the value changes */
  onChange: (_value: string | number) => void;
  /** Input name attribute */
  name?: string;
  /** Input id attribute */
  id?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether to only allow integer values */
  isInteger?: boolean;
  /** Currency code e.g. "EUR", "USD" */
  currency?: string;
  /** Number of decimal places to show */
  decimalScale?: number;
  /** Placeholder text */
  placeholder?: string;
  /** Additional suffix to append after currency symbol */
  additionalSuffix?: string;
  /** Additional prefix to prepend before currency symbol */
  additionalPrefix?: string;
  /** Number formatting style */
  style?: "currency" | "decimal";
  /** Label text (if provided, will render with a label) */
  label?: string;
  /** CSS class name for the container */
  className?: string;
  /** Input height class */
  inputHeight?: string;
  /** Callback for onBlur event */
  onBlur?: () => void;
}

/**
 * A standalone formatted number input component that doesn't require a form control
 *
 * Features:
 * - Locale-aware number formatting
 * - Currency symbol handling
 * - Thousands/decimal separators
 * - Optional label
 * - No form control dependency
 *
 * @component
 * @example
 * <StandaloneFormattedInput
 *   value={marginValue}
 *   onChange={setMarginValue}
 *   placeholder="Margin"
 *   style="decimal"
 *   additionalSuffix="%"
 * />
 */
const StandaloneFormattedInput = ({
  value,
  onChange,
  name,
  id,
  disabled = false,
  isInteger = false,
  currency = "EUR",
  decimalScale = 2,
  placeholder = "0",
  additionalSuffix = "",
  additionalPrefix = "",
  style = "currency",
  label,
  className = "",
  inputHeight = "h-9",
  onBlur
}: StandaloneFormattedInputProps) => {
  const intl = useIntl();
  const [internalValue, setInternalValue] = useState<string | number>(value != null ? value : "");

  // Update internal value when prop changes
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Extract separators and currency symbol from intl
  const { thousandSeparator, decimalSeparator, currencySymbol, symbolPosition } = useMemo(() => {
    // Hard coded sample value to get the separators and currency symbol
    const formatter = intl.formatNumberToParts(1234567.89, {
      style,
      currency,
    });

    const currencyPart = formatter.find((part) => part.type === "currency");

    // Determine the position of the currency symbol by checking the index of the currency and integer parts
    const symbolPosition = formatter.findIndex((part) => part.type === "currency") >
      formatter.findIndex((part) => part.type === "integer")
      ? "suffix"
      : "prefix";

    return {
      thousandSeparator: formatter.find((part) => part.type === "group")?.value || ",",
      decimalSeparator: formatter.find((part) => part.type === "decimal")?.value || ".",
      currencySymbol: currencyPart?.value || "",
      symbolPosition,
    };
  }, [intl, currency, style]);

  // Handle value change
  const handleValueChange = (values: { floatValue?: number }) => {
    const newValue = values.floatValue ?? "";
    setInternalValue(newValue);
    onChange(newValue);
  };

  const inputField = (
    <NumericFormat
      id={id || name}
      name={name}
      value={internalValue}
      thousandSeparator={thousandSeparator}
      decimalSeparator={decimalSeparator}
      prefix={symbolPosition === "prefix" ? additionalPrefix + currencySymbol : additionalPrefix}
      suffix={symbolPosition === "suffix" ? currencySymbol + additionalSuffix : additionalSuffix}
      customInput={Input}
      disabled={disabled}
      type="text"
      decimalScale={isInteger ? 0 : decimalScale}
      valueIsNumericString={true}
      onValueChange={handleValueChange}
      onBlur={onBlur}
      placeholder={placeholder}
      className={cn(inputHeight, className)}
    />
  );

  // If label is provided, render with label
  if (label) {
    return (
      <div className={cn("space-y-2", className)}>
        <Label htmlFor={id || name} className="text-sm font-medium">
          {label}
        </Label>
        {inputField}
      </div>
    );
  }

  // Otherwise just return the input
  return inputField;
};

export default StandaloneFormattedInput;
